import React from 'react';
import PropTypes from 'prop-types';
import {
	compose,
	withStateHandlers,
	withPropsOnChange,
	withHandlers,
	lifecycle,
	getContext,
} from 'recompose';
import classnames from 'classnames';
import { isLarge } from 'utils/breakpoints';
import FacilityInfoWindow from './FacilityInfoWindow';
import CustomOverlayComponent from 'components/core/library/map/customOverlayComponent';
import { Text } from 'components/core/Text';
import withViewportWidth from 'decorators/withViewportWidth';
import { MAP, GOOGLE } from 'components/core/library/map/constants';

export const enhance = compose(
	withViewportWidth,
	getContext({
		[MAP]: PropTypes.any,
		[GOOGLE]: PropTypes.any,
	}),
	withStateHandlers(
		{
			active: false,
			infoWindowOpen: false,
		},
		{
			setActive:
				(_, { zIndex: propZIndex }) =>
				(active) => ({ active, zIndex: active ? 2000 : propZIndex }),
			setInfoWindowOpen: () => (shouldOpen) => ({
				infoWindowOpen: shouldOpen,
			}),
			handlePageClick: () => () => ({ infoWindowOpen: false }),
		}
	),
	withHandlers({
		handleMarkerClick:
			({
				facility,
				onScrollToFacility,
				onMarkerHover,
				onShowMapFacilityCard,
				viewportWidth,
				[MAP]: map,
				[GOOGLE]: google,
				position,
				isNewSearchPage,
				setInfoWindowOpen,
			}) =>
			() => {
				if (onMarkerHover) {
					onMarkerHover(facility.id);
				}

				if (!isNewSearchPage) {
					// Old experience: show info window popup
					setInfoWindowOpen(true);
				} else {
					// New experience: scroll to facility or show facility card
					if (isLarge(viewportWidth)) {
						// On desktop: scroll to facility card in list
						if (onScrollToFacility) {
							onScrollToFacility(facility.id);
						}
					} else {
						// On mobile: show facility card at bottom of map and pan to marker
						if (onShowMapFacilityCard) {
							onShowMapFacilityCard(facility.id);
						}

						// Pan map on mobile to position marker above bottom card
						const hasMapObjects = google && map && position;
						if (hasMapObjects) {
							const markerLatLng = new google.maps.LatLng(
								position.lat,
								position.lng
							);

							// First center on the marker
							map.panTo(markerLatLng);

							// Then offset to prevent facility card from covering marker
							const panOffset = 250;
							setTimeout(() => map.panBy(0, panOffset), 100);
						}
					}
				}
			},
	}),
	withPropsOnChange(['highlighted'], (props) => {
		if (props.active !== !!props.highlighted) {
			props.setActive(!!props.highlighted);
		}
	}),
	withPropsOnChange(['facility'], ({ facility }) => {
		if (facility.cheapestPrice && facility.cheapestPrice.text) {
			return { label: `$${facility.cheapestPrice.text.split('.')[0]}` };
		}

		const cheapestUnitPrice = facility.units.reduce(
			(min, unit) =>
				parseInt(unit.price?.text) < parseInt(min)
					? unit.price?.text
					: min,
			facility.units[0].price?.text
		);

		return { label: `$${cheapestUnitPrice?.split('.')[0]}` };
	}),
	lifecycle({
		componentDidMount() {
			const { isPreselectedFacility, handleMarkerClick } = this.props;
			if (isPreselectedFacility) {
				handleMarkerClick();
			}
		},
	})
);

export function FacilityMarker({
	position,
	label,
	setActive,
	active,
	zIndex,
	facility,
	handleMarkerClick,
	onMarkerHover,
	onMarkerLeave,
	isNewSearchPage,
	infoWindowOpen,
	viewportWidth,
	phone,
	getLocalPhoneNumberByZipCode,
	handlePageClick,
	ariaLabel,
}) {
	const handleMouseEnter = () => {
		if (onMarkerHover) {
			onMarkerHover(facility.id);
		} else {
			// Fallback for old behavior when not using global state
			setActive(true);
		}
	};

	const handleMouseLeave = () => {
		// Only clear highlight on old search page
		// New search page keeps marker active until another is hovered
		if (!isNewSearchPage && onMarkerLeave) {
			onMarkerLeave();
		} else {
			// Fallback for old behavior when not using global state
			setActive(false);
		}
	};

	return isNewSearchPage ? (
		<CustomOverlayComponent
			position={position}
			zIndex={zIndex}
		>
			<button
				className={classnames('facility-marker-icon', {
					active,
				})}
				onMouseEnter={handleMouseEnter}
				onClick={handleMarkerClick}
				type="button"
				aria-label={ariaLabel}
			>
				<Text className="marker-label">{label}</Text>
			</button>
		</CustomOverlayComponent>
	) : (
		<div>
			<CustomOverlayComponent
				position={position}
				zIndex={zIndex}
			>
				<button
					className={classnames('facility-marker-icon', {
						active,
						hidden: infoWindowOpen,
					})}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
					onBlur={handleMouseLeave}
					onClick={handleMarkerClick}
					type="button"
					aria-label={ariaLabel}
				>
					<Text className="marker-label">{label}</Text>
				</button>
			</CustomOverlayComponent>
			{!!infoWindowOpen && (
				<FacilityInfoWindow
					idleDisabled
					viewportWidth={viewportWidth}
					position={position}
					facility={facility}
					onClickOutside={handlePageClick}
					phone={phone}
					getLocalPhoneNumberByZipCode={getLocalPhoneNumberByZipCode}
				/>
			)}
		</div>
	);
}

FacilityMarker.propTypes = {
	position: PropTypes.shape({ lat: PropTypes.number, lng: PropTypes.number }),
	label: PropTypes.string,
	setActive: PropTypes.func,
	active: PropTypes.bool,
	zIndex: PropTypes.number,
	facility: PropTypes.object,
	handleMarkerClick: PropTypes.func,
	onMarkerHover: PropTypes.func,
	onMarkerLeave: PropTypes.func,
	onScrollToFacility: PropTypes.func,
	onShowMapFacilityCard: PropTypes.func,
	viewportWidth: PropTypes.number,
	isNewSearchPage: PropTypes.bool,
	infoWindowOpen: PropTypes.bool,
	phone: PropTypes.object,
	getLocalPhoneNumberByZipCode: PropTypes.func,
	handlePageClick: PropTypes.func,
	ariaLabel: PropTypes.string,
};

export default enhance(FacilityMarker);
